import { useState } from 'react'
import { X, ExternalLink, ArrowRight, ArrowLeft, FileText, ChevronDown, ChevronUp } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { api } from '@/lib/api'
import { KG_NODE_STYLES, DEFAULT_NODE_STYLE } from './kg-constants'

interface KgNodeDetailPanelProps {
  nodeId: string | null
  onClose: () => void
  onNodeSelect: (nodeId: string) => void
}

export function KgNodeDetailPanel({ nodeId, onClose, onNodeSelect }: KgNodeDetailPanelProps) {
  const navigate = useNavigate()
  const [showPreview, setShowPreview] = useState(false)

  const { data: detail, isLoading } = useQuery({
    queryKey: ['kg-node-detail', nodeId],
    queryFn: () => api.kg.browser.nodeDetail(nodeId!),
    enabled: !!nodeId,
  })

  // Fetch document preview when it's a Document node and user expands preview
  const isDocument = detail?.label === 'Document'
  const { data: docPreview, isLoading: previewLoading } = useQuery({
    queryKey: ['kg-document-preview', nodeId],
    queryFn: () => api.kg.browser.documentPreview(nodeId!),
    enabled: !!nodeId && isDocument && showPreview,
    staleTime: 60000,
  })

  if (!nodeId) return null

  const style = detail ? (KG_NODE_STYLES[detail.label] || DEFAULT_NODE_STYLE) : DEFAULT_NODE_STYLE

  return (
    <div className="w-80 border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-y-auto flex-shrink-0">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className="w-3 h-3 rounded-full flex-shrink-0"
            style={{ backgroundColor: style.color }}
          />
          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
            {detail?.label ? formatLabel(detail.label) : '...'}
          </span>
        </div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
        >
          <X className="w-4 h-4 text-gray-500" />
        </button>
      </div>

      {isLoading ? (
        <div className="p-4 text-xs text-gray-500 dark:text-gray-400">Loading...</div>
      ) : detail ? (
        <div className="p-3 space-y-4">
          {/* Name */}
          <h3 className="font-semibold text-sm text-gray-900 dark:text-white">
            {detail.caption}
          </h3>

          {/* Detail page link */}
          {detail.detailUrl && (
            <button
              onClick={() => navigate(detail.detailUrl!)}
              className="flex items-center gap-1.5 text-xs text-blue-600 dark:text-blue-400 hover:underline"
            >
              <ExternalLink className="w-3 h-3" />
              {isDocument ? 'Open Customer Page' : 'Open Detail Page'}
            </button>
          )}

          {/* Document-specific actions */}
          {isDocument && (
            <div className="space-y-2">
              <button
                onClick={() => setShowPreview(prev => !prev)}
                className="flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400 hover:underline w-full"
              >
                <FileText className="w-3 h-3" />
                <span>{showPreview ? 'Hide Content Preview' : 'View Content Preview'}</span>
                {showPreview ? <ChevronUp className="w-3 h-3 ml-auto" /> : <ChevronDown className="w-3 h-3 ml-auto" />}
              </button>

              {showPreview && (
                <div className="border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
                  {previewLoading ? (
                    <div className="p-3 text-xs text-gray-400">Loading preview...</div>
                  ) : docPreview?.preview ? (
                    <div className="max-h-64 overflow-y-auto">
                      <pre className="p-3 text-[11px] leading-relaxed text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words font-mono bg-gray-50 dark:bg-gray-950">
                        {docPreview.preview}
                      </pre>
                      {docPreview.hasMore && (
                        <div className="px-3 py-1.5 text-[10px] text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
                          Content truncated. Open customer page for full document.
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="p-3 text-xs text-gray-400">No preview available for this document type.</div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Properties */}
          <div>
            <h4 className="text-[10px] uppercase font-semibold text-gray-400 dark:text-gray-500 mb-1.5 tracking-wider">
              Properties
            </h4>
            <div className="space-y-1">
              {Object.entries(detail.properties)
                .filter(([key]) => key !== 'id' && key !== 'syncedAt')
                .map(([key, value]) => (
                  <div key={key} className="flex gap-2 text-xs">
                    <span className="text-gray-500 dark:text-gray-400 flex-shrink-0 w-24 truncate">
                      {formatPropertyKey(key)}
                    </span>
                    <span className="text-gray-900 dark:text-gray-200 truncate">
                      {formatPropertyValue(value)}
                    </span>
                  </div>
                ))}
            </div>
          </div>

          {/* Relationships */}
          {detail.relationships.length > 0 && (
            <div>
              <h4 className="text-[10px] uppercase font-semibold text-gray-400 dark:text-gray-500 mb-1.5 tracking-wider">
                Relationships ({detail.relationships.length})
              </h4>
              <div className="space-y-1">
                {detail.relationships.map((rel, i) => (
                  <button
                    key={`${rel.type}-${rel.targetId}-${i}`}
                    onClick={() => onNodeSelect(rel.targetId)}
                    className="flex items-center gap-1.5 text-xs w-full text-left px-2 py-1 rounded hover:bg-gray-50 dark:hover:bg-gray-800 group"
                  >
                    {rel.direction === 'out' ? (
                      <ArrowRight className="w-3 h-3 text-gray-400 flex-shrink-0" />
                    ) : (
                      <ArrowLeft className="w-3 h-3 text-gray-400 flex-shrink-0" />
                    )}
                    <span className="text-gray-400 dark:text-gray-500 font-mono text-[10px] flex-shrink-0">
                      {rel.type}
                    </span>
                    <span className="text-gray-700 dark:text-gray-300 group-hover:text-blue-600 dark:group-hover:text-blue-400 truncate">
                      {rel.targetCaption}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="p-4 text-xs text-gray-500 dark:text-gray-400">Node not found</div>
      )}
    </div>
  )
}

function formatLabel(label: string): string {
  return label.replace(/([A-Z])/g, ' $1').trim()
}

function formatPropertyKey(key: string): string {
  return key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim()
}

function formatPropertyValue(value: unknown): string {
  if (value === null || value === undefined) return '-'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}
